/**
 * OFFSearchDialog
 *
 * A modal dialog that lets the user search Open Food Facts either by
 * product name or by entering a barcode manually.
 *
 * On selection the caller receives a `ParsedOFFProduct` that can be used
 * to pre-fill the Add/Edit food form.
 *
 * Bug fixes applied (v2):
 *  - Layout: results list no longer overlays the search bar. The dialog uses
 *    a proper flex column layout where the search bar is sticky at the top and
 *    the results area scrolls independently below it.
 *  - Stale query: handleSearch now receives the query as a parameter so it
 *    always uses the current value rather than a potentially stale closure.
 */

import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Search,
  Barcode,
  Loader2,
  AlertCircle,
  CheckCircle2,
} from "lucide-react";
import { useTranslation } from "@/hooks/useTranslation";
import {
  fetchProductByBarcode,
  searchProductsByName,
  parseOFFProduct,
  type OFFProduct,
  type ParsedOFFProduct,
} from "@/lib/openFoodFacts";

// ─── Props ────────────────────────────────────────────────────────────────────

interface OFFSearchDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called when the user confirms a product selection */
  onSelect: (product: ParsedOFFProduct) => void;
}

// ─── Nutrient preview row ─────────────────────────────────────────────────────

function NutrientRow({
  label,
  value,
  unit = "g",
}: {
  label: string;
  value?: string;
  unit?: string;
}) {
  if (!value) return null;
  return (
    <div className="flex justify-between text-sm py-0.5">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium tabular-nums">
        {value} {unit}
      </span>
    </div>
  );
}

// ─── Product card ─────────────────────────────────────────────────────────────

function ProductCard({
  product,
  parsed,
  onSelect,
  t,
}: {
  product: OFFProduct;
  parsed: ParsedOFFProduct;
  onSelect: () => void;
  t: (key: string) => string;
}) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="border rounded-lg p-3 space-y-2 hover:bg-muted/30 transition-colors">
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <p className="font-medium text-sm leading-tight truncate">
            {parsed.nazov || t("foods:off.unknownProduct")}
          </p>
          {product.brands && (
            <p className="text-xs text-muted-foreground truncate">
              {product.brands}
            </p>
          )}
          <div className="flex flex-wrap gap-1 mt-1">
            {product.quantity && (
              <Badge variant="secondary" className="text-xs">
                {product.quantity}
              </Badge>
            )}
            {product.nutrition_grades && (
              <Badge variant="outline" className="text-xs font-mono uppercase">
                {t("foods:off.nutriScore")}:{" "}
                {product.nutrition_grades.toUpperCase()}
              </Badge>
            )}
            <Badge variant="outline" className="text-xs font-mono">
              {product.code}
            </Badge>
          </div>
        </div>
        <div className="flex flex-col gap-1 shrink-0">
          <Button size="sm" onClick={onSelect}>
            <CheckCircle2 className="h-3.5 w-3.5 mr-1" />
            {t("foods:off.useProduct")}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setExpanded(v => !v)}
          >
            {expanded ? t("foods:off.hideDetails") : t("foods:off.showDetails")}
          </Button>
        </div>
      </div>

      {expanded && (
        <div className="border-t pt-2 grid grid-cols-2 gap-x-4">
          <div>
            <p className="text-xs font-semibold text-muted-foreground mb-1 uppercase tracking-wide">
              {t("foods:off.per100g")}
            </p>
            <NutrientRow
              label={t("foods:dialog.energyKj")}
              value={parsed.energiaKj}
              unit="kJ"
            />
            <NutrientRow
              label={t("foods:off.energyKcal")}
              value={parsed.energiaKcal}
              unit="kcal"
            />
            <NutrientRow
              label={t("foods:dialog.proteinG")}
              value={parsed.proteinG}
            />
            <NutrientRow
              label={t("foods:dialog.carbohydratesG")}
              value={parsed.sacharidG}
            />
            <NutrientRow
              label={t("foods:dialog.sugarG")}
              value={parsed.cukorG}
            />
          </div>
          <div>
            <p className="text-xs font-semibold text-muted-foreground mb-1 uppercase tracking-wide">
              &nbsp;
            </p>
            <NutrientRow label={t("foods:dialog.fatG")} value={parsed.tukG} />
            <NutrientRow
              label={t("foods:dialog.saturatedFatG")}
              value={parsed.nmkG}
            />
            <NutrientRow
              label={t("foods:dialog.fiberG")}
              value={parsed.vlakninaG}
            />
            <NutrientRow
              label={t("foods:dialog.sodiumG")}
              value={parsed.sodikG}
            />
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Main dialog ──────────────────────────────────────────────────────────────

export function OFFSearchDialog({
  open,
  onOpenChange,
  onSelect,
}: OFFSearchDialogProps) {
  const { t } = useTranslation();

  // Search tab state
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<OFFProduct[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [searchDone, setSearchDone] = useState(false);

  // Barcode tab state
  const [barcodeInput, setBarcodeInput] = useState("");
  const [barcodeLoading, setBarcodeLoading] = useState(false);
  const [barcodeError, setBarcodeError] = useState<string | null>(null);
  const [barcodeResult, setBarcodeResult] = useState<OFFProduct | null>(null);

  // ── Handlers ────────────────────────────────────────────────────────────────

  /**
   * Bug fix: accept the current query as a parameter so we always use the
   * value that was in the input at call time, not a potentially stale closure.
   */
  const handleSearch = async (query: string) => {
    const q = query.trim();
    if (!q) return;
    setSearchLoading(true);
    setSearchError(null);
    setSearchDone(false);
    setSearchResults([]);
    try {
      const results = await searchProductsByName(q);
      setSearchResults(results);
      setSearchDone(true);
    } catch {
      setSearchError(t("foods:off.searchError"));
    } finally {
      setSearchLoading(false);
    }
  };

  const handleBarcodeSearch = async () => {
    const code = barcodeInput.trim();
    if (!code) return;
    setBarcodeLoading(true);
    setBarcodeError(null);
    setBarcodeResult(null);
    try {
      const product = await fetchProductByBarcode(code);
      if (!product) {
        setBarcodeError(t("foods:off.barcodeNotFound"));
      } else {
        setBarcodeResult(product);
      }
    } catch {
      setBarcodeError(t("foods:off.searchError"));
    } finally {
      setBarcodeLoading(false);
    }
  };

  const handleSelect = (product: OFFProduct) => {
    onSelect(parseOFFProduct(product));
    onOpenChange(false);
    // Reset state for next open
    setSearchQuery("");
    setSearchResults([]);
    setSearchDone(false);
    setBarcodeInput("");
    setBarcodeResult(null);
    setBarcodeError(null);
    setSearchError(null);
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/*
        Layout fix: DialogContent uses `grid` by default which causes children
        to stack without proper height constraints. We switch to `flex flex-col`
        and set an explicit max-height so the inner scroll area works correctly
        and the search bar stays anchored at the top.
      */}
      <DialogContent
        className="max-w-2xl flex flex-col"
        style={{ maxHeight: "85vh" }}
      >
        <DialogHeader className="shrink-0">
          <DialogTitle>{t("foods:off.dialogTitle")}</DialogTitle>
          <DialogDescription>
            {t("foods:off.dialogDescription")}
          </DialogDescription>
        </DialogHeader>

        <Tabs
          defaultValue="search"
          className="flex flex-col flex-1 min-h-0 overflow-hidden"
        >
          {/* Tab switcher – never scrolls away */}
          <TabsList className="w-full shrink-0">
            <TabsTrigger value="search" className="flex-1">
              <Search className="h-4 w-4 mr-1.5" />
              {t("foods:off.tabSearch")}
            </TabsTrigger>
            <TabsTrigger value="barcode" className="flex-1">
              <Barcode className="h-4 w-4 mr-1.5" />
              {t("foods:off.tabBarcode")}
            </TabsTrigger>
          </TabsList>

          {/* ── Search by name ── */}
          <TabsContent
            value="search"
            className="flex flex-col flex-1 min-h-0 mt-3 overflow-hidden"
          >
            {/* Search bar – sticky, never scrolls */}
            <div className="flex gap-2 mb-3 shrink-0">
              <Input
                placeholder={t("foods:off.searchPlaceholder")}
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                onKeyDown={e => {
                  if (e.key === "Enter") handleSearch(searchQuery);
                }}
                autoFocus
              />
              <Button
                onClick={() => handleSearch(searchQuery)}
                disabled={searchLoading || !searchQuery.trim()}
              >
                {searchLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Search className="h-4 w-4" />
                )}
                <span className="ml-1.5">{t("foods:off.search")}</span>
              </Button>
            </div>

            {/* Results – scrollable area below the search bar */}
            <div className="flex-1 overflow-y-auto space-y-2 pr-1">
              {searchError && (
                <div className="flex items-center gap-2 text-destructive text-sm p-3 border border-destructive/30 rounded-md bg-destructive/5">
                  <AlertCircle className="h-4 w-4 shrink-0" />
                  {searchError}
                </div>
              )}

              {searchDone && searchResults.length === 0 && !searchError && (
                <div className="text-center text-muted-foreground py-8 text-sm">
                  {t("foods:off.noResults")}
                </div>
              )}

              {searchResults.map(product => (
                <ProductCard
                  key={product.code}
                  product={product}
                  parsed={parseOFFProduct(product)}
                  onSelect={() => handleSelect(product)}
                  t={t}
                />
              ))}
            </div>
          </TabsContent>

          {/* ── Barcode entry ── */}
          <TabsContent
            value="barcode"
            className="flex flex-col flex-1 min-h-0 mt-3 overflow-y-auto"
          >
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label htmlFor="barcode-input">
                  {t("foods:off.barcodeLabel")}
                </Label>
                <div className="flex gap-2">
                  <Input
                    id="barcode-input"
                    placeholder={t("foods:off.barcodePlaceholder")}
                    value={barcodeInput}
                    onChange={e => setBarcodeInput(e.target.value)}
                    onKeyDown={e => e.key === "Enter" && handleBarcodeSearch()}
                  />
                  <Button
                    onClick={handleBarcodeSearch}
                    disabled={barcodeLoading || !barcodeInput.trim()}
                  >
                    {barcodeLoading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Search className="h-4 w-4" />
                    )}
                    <span className="ml-1.5">{t("foods:off.lookup")}</span>
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  {t("foods:off.barcodeHint")}
                </p>
              </div>

              {barcodeError && (
                <div className="flex items-center gap-2 text-destructive text-sm p-3 border border-destructive/30 rounded-md bg-destructive/5">
                  <AlertCircle className="h-4 w-4 shrink-0" />
                  {barcodeError}
                </div>
              )}

              {barcodeResult && (
                <ProductCard
                  product={barcodeResult}
                  parsed={parseOFFProduct(barcodeResult)}
                  onSelect={() => handleSelect(barcodeResult)}
                  t={t}
                />
              )}
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
